import { TransitionInEffect } from '@goldcast/player-config/src/types/transitions';
import {
  TextElementTextStyle,
  TextElementTransitions
} from '@goldcast/player-config/src/types/RemotionPlayerConfigTypes/Element/TextElement';
import { PositionStyleProperties } from '@goldcast/player-config/src/types/RemotionPlayerConfigTypes/Style/Position';
import { SizeType } from '@goldcast/player-config/src/types/asset-types';

export const DEFAULT_CALLOUT_DURATION_SEC = 4;

export const determineTheme = (color: string): 'light' | 'dark' => {
  let r: number;
  let g: number;
  let b: number;

  if (color.startsWith('#')) {
    const hex = color.replace(/^#/, '');
    const expanded =
      hex.length === 3
        ? hex
            .split('')
            .map(c => c + c)
            .join('')
        : hex;
    const bigint = parseInt(expanded, 16);
    r = (bigint >> 16) & 255;
    g = (bigint >> 8) & 255;
    b = bigint & 255;
  } else if (color.startsWith('rgb')) {
    const parts = color.match(/\d+/g);
    r = parts ? Number(parts[0]) : 255;
    g = parts ? Number(parts[1]) : 255;
    b = parts ? Number(parts[2]) : 255;
  } else {
    return 'light';
  }

  return r * 0.2126 + g * 0.7152 + b * 0.0722 > 128 ? 'light' : 'dark';
};

export const getContrastTextColor = (backgroundColor: string): string =>
  determineTheme(backgroundColor) === 'light' ? '#000000' : '#FFFFFF';

export const calculateFontSizeBasedOnWordCount = (layout?: SizeType): number => {
  if (layout === 'LANDSCAPE') {
    return 84;
  }

  return 72;
};

export const calculateBackgroundHeightBasedOnWordCount = (text: string): number => {
  if (!text || !text.trim()) {
    return 0;
  }
  const wordCount = text.trim().split(/\s+/).length;
  if (wordCount >= 8) {
    return 19;
  }
  if (wordCount >= 5) {
    return 29;
  }
  return 49;
};

export interface CalloutStylesResult {
  containerStyle: Partial<PositionStyleProperties & { textAlign: 'center'; zIndex: number }>;
  style: TextElementTextStyle;
  transitions: TextElementTransitions;
}

export const buildCalloutStyles = (
  text: string,
  brandColor: string,
  fontFamily?: string,
  source?: 'AI-EDITS' | 'MANUAL-CREATION',
  containerWidth?: number,
  videoLayout?: SizeType
): CalloutStylesResult => {
  const textColor = '#FFFFFF';
  const shadowColor = '#000000';

  const fontSize = calculateFontSizeBasedOnWordCount(videoLayout);
  const fontFamilyValue = fontFamily || 'Inter';

  const width = videoLayout === 'LANDSCAPE' ? '60%' : '75%';
  const left = videoLayout === 'LANDSCAPE' ? '20%' : '12.5%';

  let top: string;
  if (videoLayout === 'PORTRAIT') {
    top = '80%';
  } else {
    top = '70%';
  }

  const containerStyle: CalloutStylesResult['containerStyle'] = {
    position: 'absolute',
    top,
    left,
    width,
    height: '20%',
    textAlign: 'center',
    zIndex: 1000
  };

  const style: TextElementTextStyle = {
    fontFamily: fontFamilyValue,
    fontSize,
    fontWeight: 'bold',
    color: textColor,
    lineHeight: 1.2,
    textTransform: 'uppercase',
    maxWidth: '100%',
    wordWrap: 'break-word',
    wordBreak: 'break-word',
    textShadow: `7px 7px 12px ${shadowColor}`,
    opacity: 1,
    padding: '16px',
    borderRadius: '8px',
    background: 'transparent'
  };

  const transitions: TextElementTransitions = {
    wordIn: TransitionInEffect.SlideUp
  };

  return { containerStyle, style, transitions };
};
